import { Link } from 'react-router-dom';
import type { CSSProperties, ReactNode } from 'react';
import { ENTITEIT_LINK } from './entiteitLinkStijl';

/**
 * Verwijzingen naar een auto, een persoon of een pas. Eén manier om ergens
 * naartoe te linken, zodat het niet per pagina anders gaat: de blauwe
 * onderstreepte tekst stond eerder als losse inline stijl in `PassenPage`,
 * `PasDetailPage`, `KostenPerPersoon`, `TankGedragPanel` en `WasBudgetPanel`.
 *
 * **Een echte link, geen `<span onClick>`.** Zo groeide het, en een span is geen
 * link: geen cmd- of middelklik naar een nieuw tabblad, geen rechtermuisknop,
 * niet met de tab-toets te bereiken en geen adres in de statusbalk. In een
 * beheerapp waarin je heen en weer springt tussen auto, pas en persoon is dat
 * precies wat je mist.
 *
 * **De klik stopt hier.** Veel rijen zijn zelf al klikbaar; zonder
 * `stopPropagation` doet een klik op het kenteken in een personenrij twee
 * dingen tegelijk. Dat zit nu één keer hier in plaats van in elke aanroep.
 *
 * **Zonder id platte tekst.** Veel DTO's hebben `persoonId: string | null`. Een
 * link naar `/personen/null` is erger dan geen link, dus dan valt hij terug op
 * de naam zoals hij was.
 */

const MONO = "'Roboto Mono', monospace";

const BASIS = ENTITEIT_LINK;

function EntiteitLink({
  naar,
  titel,
  mono = false,
  style,
  children,
}: {
  naar: string;
  titel: string;
  mono?: boolean;
  style?: CSSProperties;
  children: ReactNode;
}) {
  return (
    <Link
      to={naar}
      title={titel}
      onClick={(e) => e.stopPropagation()}
      style={{ ...BASIS, ...(mono ? { fontFamily: MONO } : null), ...style }}
    >
      {children}
    </Link>
  );
}

/** Naar de auto-detailpagina. Het kenteken is de sleutel in de URL, geen id. */
export function AutoLink({
  kenteken,
  style,
  children,
}: {
  kenteken: string | null | undefined;
  style?: CSSProperties;
  children?: ReactNode;
}) {
  if (!kenteken) return <span style={style}>{children ?? '—'}</span>;
  return (
    <EntiteitLink
      naar={`/auto/${encodeURIComponent(kenteken)}`}
      titel={`Naar auto ${kenteken}`}
      mono
      style={style}
    >
      {children ?? kenteken}
    </EntiteitLink>
  );
}

export function PersoonLink({
  persoonId,
  naam,
  titel,
  style,
}: {
  persoonId: string | null | undefined;
  naam: string | null | undefined;
  /** Eigen tooltip, voor waar die meer zegt dan "Naar <naam>". */
  titel?: string;
  style?: CSSProperties;
}) {
  const label = naam || '—';
  if (!persoonId || !naam) {
    return (
      <span title={titel} style={style}>
        {label}
      </span>
    );
  }
  return (
    <EntiteitLink naar={`/personen/${persoonId}`} titel={titel ?? `Naar ${naam}`} style={style}>
      {label}
    </EntiteitLink>
  );
}

export function PasLink({
  pasId,
  pasnummer,
  style,
}: {
  pasId: string | null | undefined;
  pasnummer: string | null | undefined;
  style?: CSSProperties;
}) {
  const label = pasnummer || '—';
  if (!pasId || !pasnummer) return <span style={style}>{label}</span>;
  return (
    <EntiteitLink naar={`/passen/${pasId}`} titel={`Naar pas ${pasnummer}`} mono style={style}>
      {label}
    </EntiteitLink>
  );
}
